import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Loader2, Camera, CalendarRange, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const group = (n, d = 0) => {
  const [int, dec] = Math.abs(Number(n || 0)).toFixed(d).split(".");
  const g = int.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${Number(n) < 0 ? "-" : ""}${g}${dec ? `,${dec}` : ""}`;
};
const eur = (v, d = 0) => `€${group(v, d)}`;
const signed = (v, fmt = (x) => group(x)) => (v == null ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${fmt(Math.abs(v))}`);
const deltaColor = (v) => (v == null || v === 0 ? "text-muted-foreground" : v > 0 ? "text-emerald-600" : "text-red-600");
const DeltaIcon = ({ v }) => (v == null || v === 0 ? <Minus className="w-3.5 h-3.5" /> : v > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />);

const Kpi = ({ label, value, sub, tone = "", testId }) => (
  <div className="bg-muted/30 rounded-lg p-3" data-testid={testId}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-lg sm:text-xl font-bold ${tone}`}>{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const WeeksTable = ({ weeks, year }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs sm:text-sm" data-testid="weekly-pickup-weeks-table">
      <thead>
        <tr className="border-b text-[11px] uppercase text-muted-foreground">
          <th className="text-left py-2 px-2">Semana (lunes)</th>
          <th className="text-right py-2 px-2">OTB {year}</th>
          <th className="text-right py-2 px-2">Noches</th>
          <th className="text-right py-2 px-2">Entrada semana</th>
          <th className="text-right py-2 px-2">Noches +</th>
          <th className="text-right py-2 px-2">Misma semana {year - 1}</th>
        </tr>
      </thead>
      <tbody>
        {[...weeks].reverse().map((w) => (
          <tr key={w.week_start} className="border-b hover:bg-muted/30" data-testid={`weekly-row-${w.week_start}`}>
            <td className="py-2 px-2 font-medium">
              {w.week_label}
              {w.source === "backfill" && <span className="ml-1 text-[10px] text-muted-foreground">(histórico)</span>}
            </td>
            <td className="py-2 px-2 text-right font-semibold whitespace-nowrap">{eur(w.total_revenue)}</td>
            <td className="py-2 px-2 text-right whitespace-nowrap">{group(w.total_nights)}</td>
            <td className={`py-2 px-2 text-right font-semibold whitespace-nowrap ${deltaColor(w.pickup_revenue)}`}>
              <span className="inline-flex items-center gap-1"><DeltaIcon v={w.pickup_revenue} />{signed(w.pickup_revenue, (x) => eur(x))}</span>
              {w.weeks_span > 1 && <span className="block text-[10px] font-normal text-muted-foreground">en {w.weeks_span} semanas</span>}
            </td>
            <td className={`py-2 px-2 text-right whitespace-nowrap ${deltaColor(w.pickup_nights)}`}>{signed(w.pickup_nights)}</td>
            <td className="py-2 px-2 text-right text-muted-foreground whitespace-nowrap">
              {w.ly_same_week ? (
                <span>{eur(w.ly_same_week.total_revenue)} <span className={deltaColor(w.ly_same_week.diff_revenue)}>({signed(w.ly_same_week.diff_revenue, (x) => eur(x))})</span></span>
              ) : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const MonthsTable = ({ months, year }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs sm:text-sm" data-testid="weekly-pickup-months-table">
      <thead>
        <tr className="border-b text-[11px] uppercase text-muted-foreground">
          <th className="text-left py-2 px-2">Mes</th>
          <th className="text-right py-2 px-2">OTB {year}</th>
          <th className="text-right py-2 px-2">Noches</th>
          <th className="text-right py-2 px-2">Entrada semana</th>
          <th className="text-right py-2 px-2">Final {year - 1}</th>
          <th className="text-left py-2 px-2 w-40">% ya reservado</th>
        </tr>
      </thead>
      <tbody>
        {months.map((m) => (
          <tr key={m.month} className="border-b hover:bg-muted/30" data-testid={`weekly-month-row-${m.month}`}>
            <td className="py-2 px-2 font-medium">{m.label}</td>
            <td className="py-2 px-2 text-right font-semibold whitespace-nowrap">{eur(m.otb_revenue)}</td>
            <td className="py-2 px-2 text-right whitespace-nowrap">{group(m.otb_nights)}</td>
            <td className={`py-2 px-2 text-right whitespace-nowrap ${deltaColor(m.pickup_revenue)}`}>{signed(m.pickup_revenue, (x) => eur(x))}</td>
            <td className="py-2 px-2 text-right text-muted-foreground whitespace-nowrap">{eur(m.ly_final_revenue)}</td>
            <td className="py-2 px-2">
              {m.pct_of_ly != null ? (
                <div className="flex items-center gap-2">
                  <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(m.pct_of_ly, 100)}%` }} />
                  </div>
                  <span className="text-xs font-semibold w-12 text-right">{m.pct_of_ly}%</span>
                </div>
              ) : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const WeeklyPickup = ({ defaultYear }) => {
  const [year, setYear] = useState(defaultYear || new Date().getFullYear() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/analytics/weekly-pickup?year=${year}&weeks=16`, { headers: headers() });
      setData(res.data);
    } catch {
      toast.error("Error cargando el pickup semanal");
    } finally {
      setLoading(false);
    }
  }, [year, headers]);

  useEffect(() => { load(); }, [load]);

  const snapshotNow = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/api/analytics/weekly-pickup/snapshot`, {}, { headers: headers() });
      toast.success("Foto semanal guardada");
      await load();
    } catch {
      toast.error("Error guardando la foto semanal");
    } finally {
      setSaving(false);
    }
  };

  const years = [new Date().getFullYear(), new Date().getFullYear() + 1];
  const cur = data?.current;

  return (
    <Card data-testid="weekly-pickup-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
              <CalendarRange className="w-5 h-5 text-primary" /> Pickup semanal {year}
            </CardTitle>
            <CardDescription>Foto cada lunes del on-the-books: cuánto entra cada semana y qué parte del {year - 1} ya tienes reservada</CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {years.map((y) => (
              <button
                key={y}
                type="button"
                onClick={() => setYear(y)}
                data-testid={`weekly-pickup-year-${y}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${y === year ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted border-border"}`}
              >
                {y}
              </button>
            ))}
            <Button variant="outline" size="sm" onClick={snapshotNow} disabled={saving} data-testid="weekly-pickup-snapshot-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
              Guardar foto ahora
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading && !data ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Cargando…</div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi label={`OTB ${year} hoy`} value={eur(cur.total_revenue)} sub={`${group(cur.total_nights)} noches · foto ${cur.snapshot_date || "—"}`} testId="weekly-kpi-otb" />
              <Kpi
                label={cur.weeks_span > 1 ? `Entrada últimas ${cur.weeks_span} semanas` : "Entrada esta semana"}
                value={signed(cur.pickup_revenue, (x) => eur(x))}
                sub={cur.prev_week_start ? `${signed(cur.pickup_nights)} noches · desde el ${cur.prev_week_start}` : "Primera foto: la próxima semana verás la entrada"}
                tone={deltaColor(cur.pickup_revenue)}
                testId="weekly-kpi-pickup"
              />
              <Kpi label={`% del ${year - 1} final`} value={cur.pct_of_ly_final != null ? `${cur.pct_of_ly_final}%` : "—"} sub={`${year - 1} cerró en ${eur(cur.ly_final_revenue)}`} testId="weekly-kpi-pct-ly" />
              <Kpi
                label={`Misma semana ${year - 1}`}
                value={cur.ly_same_week ? eur(cur.ly_same_week.total_revenue) : "—"}
                sub={cur.ly_same_week ? `${signed(cur.ly_same_week.diff_revenue, (x) => eur(x))} vs hoy` : `Sin foto del ${year - 1} en esta fecha; se irá rellenando`}
                tone={cur.ly_same_week ? deltaColor(cur.ly_same_week.diff_revenue) : ""}
                testId="weekly-kpi-ly-week"
              />
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">Semana a semana ({data.weeks_available} fotos)</p>
              {data.weeks.length ? <WeeksTable weeks={data.weeks} year={year} /> : <p className="text-sm text-muted-foreground">Aún no hay fotos para {year}.</p>}
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">Por mes · qué entró esta semana y cuánto del {year - 1} llevas reservado</p>
              <MonthsTable months={data.by_month} year={year} />
            </div>
            <p className="text-[11px] text-muted-foreground">
              La foto se toma automáticamente cada lunes a las 06:00 y también al actualizar los datos de producción desde Oscar. "Entrada semana" = diferencia con la foto anterior.
            </p>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default WeeklyPickup;
